import type { Configuration, MillageInformation } from '~/types';
import { generateSpecificShape, type SimplifiedProjectData } from './general-processing';

/**
 * Gets a spread of first-fit candidates from the master shape list.
 * Now samples 5 candidates at 0%, 25%, 50%, 75%, and 100% of the shape range
 * for better coverage and higher likelihood of finding viable candidates.
 * @param projectData The project data (simplified for demo)
 * @param configuration The candidate configuration
 * @returns Array of MillageInformation objects for first-fit candidates
 */
export function getFirstFitCandidates(
  projectData: SimplifiedProjectData,
  configuration: Configuration
): MillageInformation[] {
  const millageShapeOptions: MillageInformation[] = [];

  // Sample 5 candidates across the shape spectrum:
  // Total shapes: 53 (log-decel-26 to log-decel-01, linear-01, log-accel-01 to log-accel-26)
  // Positions: 0% (log-decel-26), 25% (log-decel-07), 50% (linear-01), 75% (log-accel-07), 100% (log-accel-26)
  const firstFitShapeIds = [
    'log-decel-26', // 0% - This trends down the quickest.
    'log-decel-07', // 25%
    'linear-01', // 50% - linear
    'log-accel-07', // 75%
    'log-accel-26', // 100% - Stays high the longest and then drops to the minimum.
  ];

  for (const shapeId of firstFitShapeIds) {
    try {
      const candidate = generateSpecificShape(projectData, configuration, shapeId);
      millageShapeOptions.push(candidate);
    } catch (error) {
      console.error(`Error generating shape ${shapeId}:`, error);
    }
  }

  return millageShapeOptions;
}
